import { Button } from '@accure/ui';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/ui/components/select';

export type SystemLogsPaginationProps = {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  disabled?: boolean;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

type PaginationNavProps = {
  page: number;
  totalPages?: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
};

function PaginationNav({
  page,
  totalPages,
  onPageChange,
  disabled = false,
  hasNextPage,
  hasPreviousPage,
}: PaginationNavProps) {
  // WORKAROUND: API returns per-page count instead of total count
  // Use next/previous presence when available, fallback to page calculation
  const prevDisabled =
    disabled || (hasPreviousPage !== undefined ? !hasPreviousPage : page <= 1);
  const nextDisabled =
    disabled ||
    (hasNextPage !== undefined
      ? !hasNextPage
      : totalPages !== undefined && page >= totalPages);

  return (
    <div className="flex items-center gap-2">
      <Button
        size="sm"
        variant="secondary"
        disabled={prevDisabled}
        onClick={() => onPageChange(page - 1)}
        aria-label="Previous page"
      >
        Prev
      </Button>
      <div className="whitespace-nowrap">
        Page {page}
        {totalPages !== undefined && ` of ${totalPages}`}
      </div>
      <Button
        size="sm"
        variant="secondary"
        disabled={nextDisabled}
        onClick={() => onPageChange(page + 1)}
        aria-label="Next page"
      >
        Next
      </Button>
    </div>
  );
}

type PaginationRowsInfoProps = {
  startRow: number;
  endRow: number;
};

function PaginationRowsInfo({ startRow, endRow }: PaginationRowsInfoProps) {
  return (
    <span className="whitespace-nowrap text-neutral-600">
      {startRow}–{endRow}
    </span>
  );
}

type PaginationPageSizeSelectProps = {
  pageSize: number;
  onPageSizeChange: (pageSize: number) => void;
  disabled?: boolean;
};

function PaginationPageSizeSelect({
  pageSize,
  onPageSizeChange,
  disabled = false,
}: PaginationPageSizeSelectProps) {
  return (
    <div className="flex items-center gap-2">
      <span className="whitespace-nowrap text-neutral-600">Rows:</span>
      <Select
        value={String(pageSize)}
        onValueChange={(value) => onPageSizeChange(Number(value))}
        disabled={disabled}
      >
        <SelectTrigger size="sm" className="w-20">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PAGE_SIZE_OPTIONS.map((size) => (
            <SelectItem key={size} value={String(size)}>
              {size}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/**
 * SystemLogsPagination
 *
 * Breaks pagination UI into smaller, focused components for clarity and maintainability.
 */
export function SystemLogsPagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  disabled = false,
  hasNextPage,
  hasPreviousPage,
}: SystemLogsPaginationProps) {
  // WORKAROUND: API returns per-page count, not total count
  // Don't calculate totalPages when using cursor-based pagination
  const totalPages =
    hasNextPage !== undefined
      ? undefined
      : Math.max(1, Math.ceil(total / pageSize));
  const startRow = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endRow = Math.min(page * pageSize);

  return (
    <div className="flex items-center gap-4 text-sm">
      <PaginationNav
        page={page}
        totalPages={totalPages}
        onPageChange={onPageChange}
        disabled={disabled}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
      />
      <div className="flex items-center gap-2">
        <PaginationRowsInfo startRow={startRow} endRow={endRow} />
        <span className="text-neutral-400">|</span>
        <PaginationPageSizeSelect
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
          disabled={disabled}
        />
      </div>
    </div>
  );
}
